import React from 'react';
import { useLocation } from 'react-router-dom';
import { TrendingUp, Users, Zap } from 'lucide-react';
import ProductPage from '../components/seo/ProductPage';
import { isEnglishPath } from '../utils/i18nRoutes';

const ALTERNATES = [
  { hreflang: 'es', href: '/tristrash' },
  { hreflang: 'en', href: '/en/tristrash' },
  { hreflang: 'x-default', href: '/tristrash' },
];

const CONTENT = {
  es: {
    seoTitle: 'TrisTrash | Visión artificial en flota para detectar residuos | Valoriza',
    seoDescription: 'Cámaras en la flota + IA en el borde. TrisTrash detecta desbordes y residuos abandonados, los geolocaliza y cierra el ciclo en Madrid en menos de 4 horas.',
    breadcrumbItems: [{ label: 'Inicio', to: '/' }, { label: 'Tecnología', to: '/#proyectos' }, { label: 'TrisTrash' }],
    tag: 'Tecnología en campo',
    titleMain: 'TrisTrash: la calle se convierte en',
    titleAccent: 'sensor',
    lead: 'Cada camión que ya recorre la ciudad puede mirar la acera. TrisTrash convierte la flota de limpieza y recogida en una red de sensores: cámara a bordo, inferencia en el borde, aviso geolocalizado y cierre del aviso en operaciones.',
    problemaLabel: 'El problema',
    problema: 'Las rutas fijas no ven lo que pasa entre pasada y pasada. Un contenedor se desborda a las 11:00 y el servicio no vuelve hasta el día siguiente. Un residuo abandonado espera a que alguien lo reporte. El ciudadano llega antes que el parte. Eso es lo que se licita, se multa y se tuitea.',
    comoFuncionaLabel: 'Cómo funciona',
    comoFunciona: 'Cámara embarcada en vehículos que ya están en ruta. Inferencia en el borde (no hace falta mandar vídeo a la nube para decidir; stack de referencia en campo: NVIDIA Jetson Orin Nano, DeepStream y YOLO). Detección de desbordes, residuos abandonados y otros incidentes visiblemente operativos. Geolocalización del evento y alta en el flujo de trabajo (GIS / operaciones). Asignación a la unidad más cercana y cierre del ciclo. En Madrid el objetivo de retirada en los avisos cubiertos por el sistema es menos de 4 horas, 24/7.',
    cifrasLabel: 'Qué hemos medido',
    cifras: [
      { icon: Zap, value: '40%', label: 'Respuesta más rápida' },
      { icon: TrendingUp, value: '25%', label: 'Reducción de coste' },
      { icon: Users, value: '+200', label: 'Usuarios diarios' },
    ],
    paraQuienLabel: 'Para quién',
    paraQuien: 'Ayuntamientos y contratos de limpieza/recogida que necesitan evidencia, no un PowerPoint. También equipos internos de operaciones que quieren dejar de ir a ciegas entre rutas.',
    stackLabel: 'Stack',
    stack: ['Visión artificial', 'React', 'FastAPI', 'Azure', 'GIS (ArcGIS)', 'Edge NVIDIA'],
    enlacesLabel: 'Enlaces',
    enlaces: [
      { label: 'Pando — cómo se gobierna la IA en Valoriza', to: '/pando' },
      { label: 'Quién lidera el sistema', to: '/equipo/david-redondo-perez' },
      { label: 'Más tecnología', to: '/' },
    ],
    ctaText: '¿Operas un contrato urbano?',
  },
  en: {
    seoTitle: 'TrisTrash | Onboard computer vision for street waste detection | Valoriza',
    seoDescription: 'Cameras on the fleet + edge AI. TrisTrash detects overflowing bins and abandoned waste, geolocates them and closes the loop in Madrid in under 4 hours.',
    breadcrumbItems: [{ label: 'Home', to: '/en' }, { label: 'Technology', to: '/en#proyectos' }, { label: 'TrisTrash' }],
    tag: 'Field technology',
    titleMain: 'TrisTrash: the street becomes a',
    titleAccent: 'sensor',
    lead: 'Every truck already driving through the city can look at the sidewalk. TrisTrash turns the cleaning and collection fleet into a sensor network: onboard camera, edge inference, geolocated alert and closed-loop dispatch.',
    problemaLabel: 'The problem',
    problema: 'Fixed routes miss whatever happens between passes. A bin overflows at 11:00 and the service doesn\u2019t come back until the next day. Abandoned waste waits for someone to report it. The citizen gets there before the report does. That\u2019s what gets tendered, fined and tweeted about.',
    comoFuncionaLabel: 'How it works',
    comoFunciona: 'Onboard camera on vehicles that are already on route. Edge inference (no need to send video to the cloud to decide; reference field stack: NVIDIA Jetson Orin Nano, DeepStream and YOLO). Detection of overflows, abandoned waste and other visibly operational incidents. Geolocation of the event and entry into the workflow (GIS / operations). Assignment to the nearest unit and closing of the loop. In Madrid, the removal target for alerts covered by the system is under 4 hours, 24/7.',
    cifrasLabel: 'What we\u2019ve measured',
    cifras: [
      { icon: Zap, value: '40%', label: 'Faster response' },
      { icon: TrendingUp, value: '25%', label: 'Cost reduction' },
      { icon: Users, value: '+200', label: 'Daily users' },
    ],
    paraQuienLabel: 'Who it\u2019s for',
    paraQuien: 'City councils and cleaning/collection contracts that need evidence, not a slide deck. Also internal operations teams tired of flying blind between routes.',
    stackLabel: 'Stack',
    stack: ['Computer vision', 'React', 'FastAPI', 'Azure', 'GIS (ArcGIS)', 'Edge NVIDIA'],
    enlacesLabel: 'Links',
    enlaces: [
      { label: 'Pando — how AI governance works at Valoriza', to: '/en/pando' },
      { label: 'Who leads the system', to: '/en/team/david-redondo-perez' },
      { label: 'More technology', to: '/en' },
    ],
    ctaText: 'Do you run an urban contract?',
  },
};

export default function TristrashPage() {
  const { pathname } = useLocation();
  const locale = isEnglishPath(pathname) ? 'en' : 'es';
  const c = CONTENT[locale];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'TrisTrash',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: `https://innovation.valorizasm.com${locale === 'en' ? '/en/tristrash' : '/tristrash'}`,
    description: c.seoDescription,
    publisher: { '@id': 'https://innovation.valorizasm.com/#organization' },
  };

  return (
    <ProductPage
      testId="tristrash-page"
      path={locale === 'en' ? '/en/tristrash' : '/tristrash'}
      locale={locale}
      alternates={ALTERNATES}
      jsonLd={[jsonLd]}
      {...c}
    />
  );
}
